import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";

import { HospicePatientPayer } from "../../../../../../../resources-vue/vue-models/hospice-patient-payer";
import { PayerSequenceCodes, PayerTypes } from "../../../../../../../enums/enums";
import { ToastrService } from "../../../../../../../services/toastr.service";
import { formatDate } from "../../../../../../../common/vue-helpers/modifiers/value-modifier";

import TablePrompt from "../../../../../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";
import EmptyTextHandler from "../../../../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";
import PayerForm from "../PayerForm/PayerForm.vue";

export default defineComponent({
    components: {
        PayerForm,
        TablePrompt,
        EmptyTextHandler
    },
    props: {
        payer: {
            type: Object
        },
        columns: {
            type: Number
        },
        payorTypeEnum: {
            default: PayerTypes
        },
        resetList: {
            type: Function
        },
        medicareIds: {
            type: Array,
            default: []
        },
        updatePatientPayer: {
            type: Function
        },
        deletePatientPayer: {
            type: Function
        }
    },

    data(vm) {
        return {
            _toastrService: null,
            cachedData: null,
            isEditRequested: false,
            isEditInProgress: false,
            isDeleteRequested: false,
            isDeleteInProgress: false,
            payerSequenceCodeEnum: PayerSequenceCodes,
            formatDate
        };
    },
    created() {
        this._toastrService = Container.instance.get(ToastrService);
    },
    mounted() {
        this.cachedData = new HospicePatientPayer(this.payer);
    },
    computed: {
        billAsTypeLabel() {
            if (!this.payerSequenceCodeEnum || !this.payer || !this.payer.BillAsType) {
                return null;
            }
            return this.payerSequenceCodeEnum.getDescriptionUsingValue(this.payer.BillAsType);
        },
        payorTypeLabel() {
            if (!this.payorTypeEnum || !this.payer) {
                return null;
            }
            return this.payorTypeEnum?.getDescriptionUsingValue(this.payer.Type);
        },
        isUpcoming() {
            if (this.payer.EffectiveDate) {
                return moment() < moment(this.payer.EffectiveDate);
            } else {
                return false;
            }
        }
    },
    methods: {
        openEditPayer() {
            this.isEditRequested = true;
            this.cachedData = new HospicePatientPayer(this.payer);
        },
        async updatePayer() {
            try {
                this.isEditInProgress = true;
                let updateResponse = await this.updatePatientPayer(this.payer.Id, this.cachedData);
                this.payer.Id = updateResponse.data.id;
                this.closeEditPayer();
                this._toastrService.success({
                    title: `Payer Updated`,
                    message: `The Payer <b>${this.payer.AgencyPayorName}</b> has been successfully updated.`
                });
            } catch (err) {
                this.openEditPayer();
            } finally {
                this.isEditInProgress = false;
            }
        },
        closeEditPayer() {
            this.isEditRequested = false;
            this.cachedData = undefined;
        },
        confirmDeletePayer() {
            this.isDeleteRequested = true;
        },
        async deletePayer() {
            this.isDeleteInProgress = true;
            const delResult = await this.deletePatientPayer(this.payer.Id);
            if (delResult.isSuccess) {
                this.isDeleteRequested = false;
                this.resetList();
                this._toastrService.success({
                    message: `The Payer ${this.payer.AgencyPayorName} has been successfully deleted.`,
                    title: `Payer Deleted`
                });
            } else {
                this._toastrService.error({
                    title: "Failed to delete Payer",
                    message: `${JSON.parse(delResult.message).message}`
                });
                this.confirmDeletePayer();
                this.resetList();
            }
        },
        cancelDeletePayer() {
            this.isDeleteRequested = false;
        }
    }
});
